import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Card, CardContent, CircularProgress, LinearProgress, Skeleton, Typography, useMediaQuery } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const GAUGE_SIZE = 100;
const GAUGE_THICKNESS = 3;

const styles = {
  card: {
    width: '100%',
    boxShadow: 2,
    bgcolor: 'background.paper',
    cursor: 'pointer',
    height: 'auto',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    transition: 'box-shadow 0.2s',
    '&:hover': { boxShadow: 4, bgcolor: 'action.hover' },
  },
  cardContent: {
    px: 2,
    pt: { xs: 1.25, md: 1.5 },
    pb: { xs: 1.5, md: 1.75 },
    '&:last-child': { pb: { xs: 1.5, md: 1.75 } },
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: 0.75, md: 1.25 },
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 0.5,
  },
  percentSub: {
    fontSize: { xs: '0.65rem', md: '0.65rem' },
    color: 'text.secondary',
    fontWeight: 500,
  },
  label: {
    fontSize: { xs: '0.65rem', md: '0.65rem' },
    fontWeight: 700,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: 'text.disabled',
    flexShrink: 1,
    minWidth: 0,
  },
  counter: {
    fontSize: { xs: '0.65rem', md: '0.65rem' },
    fontWeight: 700,
    color: 'text.secondary',
    bgcolor: theme => theme.bruno.colors.inactive,
    px: 1,
    py: 0.5,
    borderRadius: '6px',
    flexShrink: 0,
    whiteSpace: 'nowrap',
    minWidth: 44,
    textAlign: 'center',
  },
  chevron: {
    fontSize: 18,
    color: 'text.disabled',
    flexShrink: 0,
    ml: { xs: 0.5, md: 0 },
  },
  percent: {
    fontSize: { xs: '0.75rem', md: '0.75rem' },
    fontWeight: 700,
    color: 'success.main',
    lineHeight: 1,
  },
  progressBar: {
    height: 6,
    borderRadius: 3,
    bgcolor: 'divider',
    '& .MuiLinearProgress-bar': { borderRadius: 3 },
  },
  gaugeWrap: {
    position: 'relative',
    width: GAUGE_SIZE,
    height: GAUGE_SIZE / 2,
    overflow: 'hidden',
    mx: 'auto',
  },
  gaugeInner: {
    position: 'absolute',
    top: 0,
    left: 0,
    transform: 'rotate(-90deg)',
    transformOrigin: `${GAUGE_SIZE / 2}px ${GAUGE_SIZE / 2}px`,
  },
  gaugeOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    transform: 'rotate(-90deg)',
    transformOrigin: `${GAUGE_SIZE / 2}px ${GAUGE_SIZE / 2}px`,
    '& circle': { strokeLinecap: 'round' },
  },
  gaugeLabel: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
};

export default function NavigationStatusCard({
  onOpen,
  navigationProgress,
  navigationCompleted,
  navigationPending,
  navigationLoading,
  navigationFetched,
  currentStore,
  getNavigationStatus,
}) {
  const { t } = useTranslation();
  const isMobile = useMediaQuery('(max-width:600px)');
  const prevDataRef = useRef({ progress: 0, completed: {}, pending: {} });

  useEffect(() => {
    if (currentStore) getNavigationStatus();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStore]);

  const isLoading = navigationLoading || !navigationFetched;

  if (!isLoading) {
    prevDataRef.current = { progress: navigationProgress, completed: navigationCompleted, pending: navigationPending };
  }

  const completedTotal = (isLoading ? prevDataRef.current.completed : navigationCompleted)?.total ?? 0;
  const pendingTotal = (isLoading ? prevDataRef.current.pending : navigationPending)?.total ?? 0;
  const total = completedTotal + pendingTotal;
  const percent = Math.round(((isLoading ? prevDataRef.current.progress : navigationProgress) || 0) * 100);
  const gaugeValue = percent / 2;

  return (
    <Card sx={styles.card} onClick={onOpen}>
      <CardContent sx={styles.cardContent}>

        {/* Header: label + chevron + counter */}
        <Box sx={styles.header}>
          <Typography sx={styles.label}>{isMobile ? t('bruno.navigation.status_title_short') : t('bruno.navigation.status_title')}</Typography>
          <ChevronRightIcon sx={styles.chevron} />
          <Box sx={{ flexGrow: 1 }} />
          {!isMobile && (isLoading ? (
            <Skeleton variant="rounded" width={48} height={20} sx={{ minWidth: 48 }} />
          ) : (
            <Typography sx={styles.counter}>{`${completedTotal}/${total}`}</Typography>
          ))}
        </Box>

        {/* Mobile: gauge semicircular */}
        {isMobile && (isLoading ? (
          <>
            <Skeleton variant="rounded" width="100%" height={GAUGE_SIZE / 2} sx={{ borderRadius: 2 }} />
            <Skeleton variant="rounded" width="60%" height={16} sx={{ mx: 'auto' }} />
          </>
        ) : (
          <>
            <Box sx={styles.gaugeWrap}>
              <Box sx={styles.gaugeInner}>
                <CircularProgress variant="determinate" value={50} size={GAUGE_SIZE} thickness={GAUGE_THICKNESS} sx={{ color: 'divider' }} />
              </Box>
              <Box sx={styles.gaugeOverlay}>
                <CircularProgress variant="determinate" value={gaugeValue} size={GAUGE_SIZE} thickness={GAUGE_THICKNESS} color="success" />
              </Box>
              <Box sx={styles.gaugeLabel}>
                <Typography sx={styles.percent}>{`${percent}%`}</Typography>
              </Box>
            </Box>
            <Typography sx={styles.percentSub}>{t('bruno.navigation.status_sub3')}</Typography>
          </>
        ))}

        {/* Desktop: percent + sub + progress bar */}
        {!isMobile && (isLoading ? (
          <>
            <Skeleton variant="rounded" width="60%" height={40} />
            <Skeleton variant="rounded" height={6} sx={{ borderRadius: 3 }} />
          </>
        ) : (
          <>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
              <Typography sx={{ fontSize: { xs: '2rem', md: '2rem' }, fontWeight: 700, color: 'success.main', lineHeight: 1 }}>{`${percent}%`}</Typography>
              <Typography sx={styles.percentSub}>{t('bruno.navigation.status_sub3')}</Typography>
            </Box>
            <LinearProgress variant="determinate" value={percent} color="success" sx={styles.progressBar} />
          </>
        ))}

      </CardContent>
    </Card>
  );
}
